import { DynamicInputBlock } from '../scoremodel/modules/dynamicInputBlock';
import React, { useState } from 'react';
import { useController, useResource } from 'rest-hooks';
import { VariableResource } from '../../model/resources/variableResource';
import { useFilteredAndSorted } from '../../modules/filterSort/useFilteredAndSorted';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { Variable } from '../../model/interfaces/variable';
import { Stack } from '@mui/material';
import { TitledPaperWithAddButton } from '../../modules/components/titledPaper/titledPaperWithAddButton/titledPaperWithAddButton';
import { FilterField } from '../../modules/filterSort/filterField';
import { ScrollableStack } from '../../modules/components/scrollableStack';
import { VariableListItem } from './modules/variableListItem';
import { VariablesList } from './modules/variablesList';

export const InputBlockVarables = () => {
  const VariablesPage = () => {
    const [isCreatingNewVariable, setIsCreatingNewVariable] = useState<boolean>(false);

    const variables = useResource(VariableResource.list(), {});
    const [filteredandSortedVariables, setFilter] = useFilteredAndSorted(variables);

    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const { fetch } = useController();

    /**
     * @description Update an existing variable
     * @param updatedVariable The variable to update
     */
    const updateVariable = async (updatedVariable: Variable) => {
      const id = updatedVariable.variableId;
      await fetch(VariableResource.update(), { variableId: id }, { ...updatedVariable })
        .then((result) => {
          enqueueSnackbar(
            intl.formatMessage({
              id: 'snackbar.variable.update.success',
              defaultMessage: 'Variable updated'
            }),
            { variant: 'success' }
          );
          return result;
        })
        .catch((e) =>
          enqueueSnackbar(
            intl.formatMessage({
              id: 'snackbar.variable.update.error',
              defaultMessage: 'Could not update variable'
            }),
            { variant: 'error' }
          )
        );
    };

    /**
     * Create a new variable
     * @param newVariable The new variable to create
     */
    const createVariable = async (newVariable: Variable) => {
      try {
        await fetch(VariableResource.create(), { ...newVariable });
        enqueueSnackbar(
          intl.formatMessage({
            id: 'snackbar.variable.create.success',
            defaultMessage: 'New variable created'
          }),
          { variant: 'success' }
        );
        setIsCreatingNewVariable(false);
      } catch (e) {
        enqueueSnackbar(
          intl.formatMessage({
            id: 'snackbar.variable.create.error',
            defaultMessage: 'Could not create new variable'
          }),
          { variant: 'error' }
        );
      }
    };

    /**
     * @description Deletes the variable based on its id
     * @param variable The variable to be deleted
     */
    const deleteVariable = async (variable: Variable) => {
      const id = variable.variableId;
      await fetch(VariableResource.delete(), { variableId: id })
        .then((result) => {
          enqueueSnackbar(
            intl.formatMessage({
              id: 'snackbar.variable.delete.success',
              defaultMessage: 'Variable deleted'
            }),
            { variant: 'success' }
          );
          return result;
        })
        .catch((e) =>
          enqueueSnackbar(
            intl.formatMessage({
              id: 'snackbar.variable.delete.error',
              defaultMessage: 'Could not delete variable'
            }),
            { variant: 'error' }
          )
        );
    };

    return (
      <Stack direction="row" spacing={2} height="inherit" style={{ margin: '10px' }}>
        <TitledPaperWithAddButton
          columnId="inputBlock"
          initialColumnWidth={0.4}
          title={intl.formatMessage({
            id: 'Variables'
          })}
          onClick={() => setIsCreatingNewVariable(true)}>
          <FilterField setFilter={setFilter} />
          <ScrollableStack spacing={1} style={{ height: 'inherit', overflow: 'auto' }}>
            {isCreatingNewVariable && (
              <VariableListItem
                isOpen
                deleteVariable={() => setIsCreatingNewVariable(false)}
                saveVariable={(variable) => createVariable(variable)}
                onToggle={() => setIsCreatingNewVariable(false)}
                variable={VariableResource.fromJS()}
              />
            )}
            <VariablesList
              variables={filteredandSortedVariables}
              saveVariable={updateVariable}
              deleteVariable={deleteVariable}
            />
          </ScrollableStack>
        </TitledPaperWithAddButton>
      </Stack>
    );
  };
  return <DynamicInputBlock LastComponent={VariablesPage} columnWidth={0.4} />;
};
